import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "./icon";
import { parseRelative, stepRelative } from "./relative_number";
import { commitOf, parseTyped } from "./typed_number";

const STEP =
	"inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-line bg-surface-sunken font-bold text-ink hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-40";
export const NUMBER_INPUT = "cs-num h-11 w-20 rounded-lg border border-line bg-surface-raised px-2 text-center text-xl text-ink disabled:opacity-60";

type Draft = { text: string; from: number };

// A typed whole number that commits on change, Enter or blur (Escape drops what was typed). While it holds typed text it
// shows that text whatever `value` becomes, so a diff from another session never rewrites a number being typed; the
// commit says what the field started from (`from`), for a write that is a delta. `steps` adds the −/+ buttons for ±1.
// `relative` (the PV fields) keeps a leading + or − while typing: "-7" and "+3" go to `onDelta`, a plain number to `onCommit`;
// it is a text box (a numeric keypad has no sign), so ArrowUp and ArrowDown step by one within min..max themselves.
export function NumberField({
	value,
	label,
	min,
	max,
	disabled = false,
	steps,
	relative = false,
	class: className = NUMBER_INPUT,
	onCommit,
	onDelta,
}: {
	value: number;
	label: string;
	min?: number;
	max?: number;
	disabled?: boolean;
	steps?: { less: string; more: string };
	class?: string;
	relative?: boolean;
	onCommit: (next: number, from: number) => void;
	onDelta?: (delta: number) => void;
}) {
	// A ref, not state: Enter commits and the change event it also fires must find the draft already gone.
	const draft = useRef<Draft | null>(null);
	const [, render] = useState(0);
	const bounds = { min, max };
	const shown = draft.current?.text ?? String(value);

	const commit = () => {
		const typed = draft.current;
		draft.current = null;
		render((n) => n + 1);
		const entry = typed && relative ? parseRelative(typed.text) : undefined;
		if (entry === null) return;
		if (entry && "delta" in entry) return onDelta?.(entry.delta);
		const commitment = typed && commitOf(typed.text, typed.from, bounds);
		if (commitment) onCommit(commitment.next, commitment.from);
	};
	// Native `change`, which `onChange` is not: once preact/compat is loaded it makes `onChange` fire on every input event.
	const input = useRef<HTMLInputElement>(null);
	const committing = useRef(commit);
	committing.current = commit;
	useEffect(() => {
		const element = input.current;
		const onChange = () => committing.current();
		element?.addEventListener("change", onChange);
		return () => element?.removeEventListener("change", onChange);
	}, []);
	const step = (delta: number) => {
		draft.current = null;
		const next = parseTyped(String(value + delta), bounds);
		if (next !== null && next !== value) onCommit(next, value);
		render((n) => n + 1);
	};

	return (
		<>
			{steps && (
				<button type="button" aria-label={steps.less} disabled={disabled || (min !== undefined && value <= min)} class={STEP} onClick={() => step(-1)}>
					<Icon name="minus" class="h-3.5 w-3.5" />
				</button>
			)}
			<input
				ref={input}
				{...(relative ? { type: "text" as const } : { type: "number" as const })}
				inputMode={relative ? "text" : "numeric"}
				autocomplete="off"
				min={min}
				max={max}
				aria-label={label}
				disabled={disabled}
				value={shown}
				class={className}
				onInput={(event) => {
					draft.current = { text: event.currentTarget.value, from: draft.current?.from ?? value };
					render((n) => n + 1);
				}}
				onBlur={commit}
				onKeyDown={(event) => {
					if (event.key === "Enter") commit();
					else if (relative && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
						event.preventDefault();
						const direction = event.key === "ArrowUp" ? 1 : -1;
						const text = draft.current && stepRelative(draft.current.text, direction);
						if (draft.current && text !== null) {
							draft.current = { ...draft.current, text };
							render((n) => n + 1);
						} else step(direction);
					} else if (event.key === "Escape" && draft.current) {
						event.preventDefault();
						draft.current = null;
						render((n) => n + 1);
					}
				}}
			/>
			{steps && (
				<button type="button" aria-label={steps.more} disabled={disabled || (max !== undefined && value >= max)} class={STEP} onClick={() => step(1)}>
					<Icon name="plus" class="h-3.5 w-3.5" />
				</button>
			)}
		</>
	);
}
